/**
 * One box of a hand-built ICS card, with its own Save and Cancel.
 *
 * The shadcn replacement for `PratusTextArea` and `PratusInput`, which carry
 * the same pair. Saving per box rather than per card is what lets a card write
 * the whole incident info record on every commit without a second editor's
 * unsaved work riding along.
 *
 * The draft is held locally so a refetch cannot overwrite a half-typed
 * sentence: server state is taken only while the box has no unsaved edits.
 *
 * The pair appears only while the box is being worked on, which is focus or an
 * uncommitted edit.
 */

import type React from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  refocusEditScope,
  useEditScopeFocus,
} from 'components/IAP/hooks/useEditScopeFocus';

export type SaveableFieldProps = {
  id: string;
  label: string;
  value: string | null | undefined;
  onSave: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  /** Native input type, for the date boxes. Ignored when multiline. */
  type?: string;
  /** Character ceiling the printed form imposes on the box. */
  maxLength?: number;
  /** Validation message shown under the box, owned by the caller. */
  error?: string | null;
  description?: string;
  /**
   * Hides the label from sight for a box whose numbered title already names it.
   * The label still reaches screen readers and still names the buttons.
   */
  hideLabel?: boolean;
};

export const SaveableField: React.FC<SaveableFieldProps> = ({
  id,
  label,
  value,
  onSave,
  placeholder,
  disabled = false,
  multiline = false,
  rows = 3,
  type,
  maxLength,
  error,
  description,
  hideLabel = false,
}) => {
  const serverValue = value ?? '';
  const [draft, setDraft] = useState(serverValue);
  // What the box was last known to hold. Kept in state rather than a ref so
  // committing re-renders and the pair can settle.
  const [baseline, setBaseline] = useState(serverValue);
  const { focused, focusProps } = useEditScopeFocus();

  useEffect(() => {
    setDraft((current) => (current === baseline ? serverValue : current));
    setBaseline(serverValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValue]);

  const dirty = draft !== baseline;

  const handleSave = () => {
    setBaseline(draft);
    onSave(draft);
    refocusEditScope(id);
  };

  const handleCancel = () => {
    setDraft(baseline);
    refocusEditScope(id);
  };

  const control = multiline ? (
    <Textarea
      id={id}
      rows={rows}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
    />
  ) : (
    <Input
      id={id}
      type={type}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
    />
  );

  return (
    <Field className="tw:gap-2" data-section={id} {...focusProps}>
      <FieldLabel htmlFor={id} className={hideLabel ? 'tw:sr-only' : undefined}>
        {label}
      </FieldLabel>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      {control}
      {error ? (
        <FieldDescription className="tw:text-destructive">
          {error}
        </FieldDescription>
      ) : null}
      {/* Dirty keeps the pair on screen after focus moves away, so an edit is
          never left with no visible way to commit or discard it. */}
      {focused || dirty ? (
        <div className="tw:flex tw:justify-end tw:gap-2" data-section-actions>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || !dirty}
            aria-label={`Cancel ${label}`}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={disabled || !dirty}
            aria-label={`Save ${label}`}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      ) : null}
    </Field>
  );
};

export default SaveableField;
